import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Property } from '../../../shared/types/Property';
import { BuildingIcon, TagIcon, MapPinIcon, PhoneIcon, GlobeIcon, ClockIcon, CheckIcon, XIcon, InfoIcon } from 'lucide-react';
import { InfoTooltip } from '../../../shared/ui/InfoTooltip';
export function PropertyInformation() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data for a Malaysian resort
  const [property, setProperty] = useState<Property>({
    name: 'Langkawi Bay Tropical Paradise Resort',
    description: 'Nestled on the pristine shores of Langkawi, Langkawi Bay Tropical Paradise Resort offers a perfect blend of Malaysian luxury and natural beauty. Surrounded by lush tropical rainforest and overlooking the crystal-clear waters of the Andaman Sea, this exclusive retreat provides an authentic Malaysian experience with modern comforts.',
    designation: 'Resort',
    starRating: 5,
    tags: ['Beach Access', 'Spa Retreat', 'Family-Friendly', 'Luxury', 'Malaysian Cuisine', 'Beachfront', 'Island Escape', 'Wellness-Retreat', 'All-Inclusive', 'Yoga Classes', 'Eco-Friendly', 'Cultural Experiences'],
    address: {
      office: '124 Jalan Pantai Cenang',
      city: 'Langkawi',
      postalCode: '07000',
      country: 'Malaysia',
      latitude: '6.2885',
      longitude: '99.7325'
    },
    contact: {
      email: 'reservations@langkawibaytropical.com',
      phone: '+60 4 955 1234',
      website: 'https://www.langkawibaytropicalresort.com',
      timezone: 'Asia/Kuala_Lumpur'
    },
    checkIn: '14:00',
    checkOut: '12:00',
    amenities: ['Infinity Pool', 'Private Pool Villas', 'WiFi', 'Airport Shuttle', 'Daily Housekeeping', 'Smart TV', 'Hair Dryer', 'In-Room Safe', 'Bicycle Rental', 'Malaysian Cooking Classes', 'Sunset Cruise', 'Island Hopping', 'Floating Breakfast', 'Traditional Malay Massage', 'Silat Classes', 'Mangrove Boat Tours'],
    transportation: ['Taxi', 'Hotel Shuttle', 'Motorbike Rental', 'Longtail Boat', 'Ferry', 'Domestic Flight', 'Private Van Transfer']
  });
  // Function to render tags
  const renderTags = (tags: string[]) => {
    return <div className="flex flex-wrap gap-2" data-id="element-8790">
        {tags.map((tag, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-8791">
            {tag}
            <button aria-label={`Remove ${tag}`} className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-8792">
              <XIcon size={12} aria-hidden="true" data-id="element-8793" />
            </button>
          </div>)}
      </div>;
  };
  // Draft information tooltip content
  const draftInfoContent = "Your hotel listing is currently in draft mode. It won't be visible until your booking engine is enabled. Once you add availability or offers through your inventory management, our team will review and publish them when ready.";
  return <div className="space-y-6" data-id="element-8794">
      {/* Simplified header section */}
      <div data-id="element-8795">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4" data-id="element-8796">
          {t('pages.PropertyInformation.hotel')}
        </h1>
        <div className="flex justify-between items-center" data-id="element-8797">
          {/* Left side - tabs */}
          <div className="border-b border-gray-200" data-id="element-8798">
            <div className="flex space-x-6" data-id="element-8799">
              <button className="text-sm font-medium text-blue-600 border-b-2 border-blue-600 pb-2" data-id="element-8800">
                {t('pages.PropertyInformation.hotel_information')}
              </button>
              <button className="text-sm font-medium text-gray-500 hover:text-gray-800 pb-2" data-id="element-8801">
                {t('components.EditRoomForm.media_assets')}
              </button>
            </div>
          </div>
          {/* Right side - status button with tooltip */}
          <div className="relative group" data-id="element-8802">
            <button className="flex items-center bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium" data-id="element-8803">
              <span className="mr-2" data-id="element-8804">
                {t('pages.PropertyInformation.draft_submitted_for_review')}
              </span>
              <InfoIcon size={16} className="text-gray-500" data-id="element-8805" />
            </button>
            <div className="absolute right-0 mt-2 w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-10 hidden group-hover:block" data-id="element-8806">
              <h3 className="text-sm font-medium text-gray-800 mb-1" data-id="element-8807">
                {t('pages.PropertyInformation.draft_status')}
              </h3>
              <p className="text-xs text-gray-600" data-id="element-8808">{draftInfoContent}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Hotel Details Form */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-8809">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-8810">
          <div data-id="element-8811">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8812">
              {t('pages.PropertyInformation.hotel_name')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.name} onChange={e => setProperty({
            ...property,
            name: e.target.value
          })} data-id="element-8813" />
          </div>
          <div data-id="element-8814">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8815">
              {t('pages.PropertyInformation.designation')}
            </label>
            <div className="relative" data-id="element-8816">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.designation} onChange={e => setProperty({
              ...property,
              designation: e.target.value
            })} data-id="element-8817">
                <option value="Resort" data-id="element-8818">
                  {t('pages.PropertyInformation.resort')}
                </option>
                <option value="Hotel" data-id="element-8819">
                  {t('pages.PropertyInformation.hotel')}
                </option>
                <option value="Villa" data-id="element-8820">
                  {t('pages.PropertyInformation.villa')}
                </option>
                <option value="Guesthouse" data-id="element-8821">
                  {t('pages.PropertyInformation.guesthouse')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-8822">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-8823" />
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-8824">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8825">
            {t('components.AddRoomForm.description')}
          </label>
          <textarea className="w-full border border-gray-300 rounded-md px-3 py-2 h-24" value={property.description} onChange={e => setProperty({
          ...property,
          description: e.target.value
        })} data-id="element-8826" />
        </div>
        <div className="mb-6" data-id="element-8827">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8828">
            {t('pages.PropertyInformation.star_rating')}
          </label>
          <div className="relative" data-id="element-8829">
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.starRating} onChange={e => setProperty({
            ...property,
            starRating: parseInt(e.target.value)
          })} data-id="element-8830">
              <option value="5" data-id="element-8831">5</option>
              <option value="4" data-id="element-8832">4</option>
              <option value="3" data-id="element-8833">3</option>
              <option value="2" data-id="element-8834">2</option>
              <option value="1" data-id="element-8835">1</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-8836">
              <ChevronDownIcon size={16} className="text-gray-400" data-id="element-8837" />
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-8838">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8839">
            {t('pages.PropertyInformation.tags')}
          </label>
          {renderTags(property.tags)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8840">
          <div data-id="element-8841">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8842">
              {t('pages.PropertyInformation.address')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.office} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              office: e.target.value
            }
          })} data-id="element-8843" />
          </div>
          <div data-id="element-8844">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8845">
              {t('pages.PropertyInformation.area_name')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value="Pantai Cenang" data-id="element-8846" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8847">
          <div data-id="element-8848">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8849">
              {t('pages.PropertyInformation.city')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.city} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              city: e.target.value
            }
          })} data-id="element-8850" />
          </div>
          <div data-id="element-8851">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8852">
              {t('pages.PropertyInformation.postal_code')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.postalCode} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              postalCode: e.target.value
            }
          })} data-id="element-8853" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-8854">
          <div data-id="element-8855">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8856">
              {t('pages.PropertyInformation.country')}
            </label>
            <div className="relative" data-id="element-8857">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.address.country} onChange={e => setProperty({
              ...property,
              address: {
                ...property.address,
                country: e.target.value
              }
            })} data-id="element-8858">
                <option value="Malaysia" data-id="element-8859">
                  {t('pages.PropertyInformation.malaysia')}
                </option>
                <option value="Indonesia" data-id="element-8860">
                  {t('pages.PropertyInformation.indonesia')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-8861">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-8862" />
              </div>
            </div>
          </div>
          <div data-id="element-8863">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8864">
              {t('pages.PropertyInformation.latitude')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.latitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              latitude: e.target.value
            }
          })} data-id="element-8865" />
          </div>
          <div data-id="element-8866">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8867">
              {t('pages.PropertyInformation.longitude')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.longitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              longitude: e.target.value
            }
          })} data-id="element-8868" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8869">
          <div data-id="element-8870">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8871">
              {t('pages.PropertyInformation.contact_email')}
            </label>
            <input type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.email} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              email: e.target.value
            }
          })} data-id="element-8872" />
          </div>
          <div data-id="element-8873">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8874">
              {t('pages.PropertyInformation.phone_number')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.phone} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              phone: e.target.value
            }
          })} data-id="element-8875" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-8876">
          <div data-id="element-8877">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8878">
              {t('pages.PropertyInformation.website')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.contact.website} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              website: e.target.value
            }
          })} data-id="element-8879" />
          </div>
          <div data-id="element-8880">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8881">
              {t('pages.PropertyInformation.timezone')}
            </label>
            <div className="relative" data-id="element-8882">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.contact.timezone} onChange={e => setProperty({
              ...property,
              contact: {
                ...property.contact,
                timezone: e.target.value
              }
            })} data-id="element-8883">
                <option value="Asia/Kuala_Lumpur" data-id="element-8884">
                  {t('pages.PropertyInformation.asiakualalumpur')}
                </option>
                <option value="Asia/Singapore" data-id="element-8885">
                  {t('pages.PropertyInformation.asiasingapore')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-8886">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-8887" />
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8888">
          <div data-id="element-8889">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8890">
              {t('pages.PropertyInformation.checkin_time')}
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkIn} onChange={e => setProperty({
            ...property,
            checkIn: e.target.value
          })} data-id="element-8891" />
          </div>
          <div data-id="element-8892">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8893">
              {t('pages.PropertyInformation.checkout_time')}
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkOut} onChange={e => setProperty({
            ...property,
            checkOut: e.target.value
          })} data-id="element-8894" />
          </div>
        </div>
        <div className="mb-6" data-id="element-8895">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8896">
            {t('pages.PropertyInformation.hotel_amenities')}
          </label>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-8897">
            <div className="flex flex-wrap gap-2" data-id="element-8898">
              {property.amenities.map((amenity, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-8899">
                  {amenity}
                  <button aria-label={`Remove ${amenity}`} className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-8900">
                    <XIcon size={12} aria-hidden="true" data-id="element-8901" />
                  </button>
                </div>)}
            </div>
          </div>
        </div>
        <div data-id="element-8902">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8903">
            {t('pages.PropertyInformation.transportation')}
          </label>
          <div className="space-y-2" data-id="element-8904">
            {property.transportation.map((item, index) => <div key={index} className="flex items-center" data-id="element-8905">
                <input type="checkbox" id={`transportation-${index}`} checked={true} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-8906" />

                <label htmlFor={`transportation-${index}`} className="ml-2 text-sm text-gray-700" data-id="element-8907">
                  {item}
                </label>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
// Helper component for the ChevronDown icon
function ChevronDownIcon({
  size = 24,
  className = ''
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-8908">
      <polyline points="6 9 12 15 18 9" data-id="element-8909"></polyline>
    </svg>;
}